import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import ChatHeader from "../../components/chat-header";
import { useLayoutContext } from "../../layouts/general-layout";
import { RouterConstant } from "../../routers/index";
import { SelfTestService } from "../../services/self-test/self-test-service";
import { examImg, arrowLeft, Loader } from "../../assets/images/index";
import { useSelfTestStore } from "../../utils/selfTestStore";
import "./self-test-summary.scss";

interface ExerciseSummary {
    exercise_id: number;
    exercise_display_title: string;
    answer_status: number; // 1: correct, 2: wrong, 3: skipped
}

export default function Index() {
    const { t } = useLayoutContext();
    const navigate = useNavigate();

    const location = useLocation();
    const stateTestId = location.state?.testId;
    const storedTestId = useSelfTestStore((s) => s.selected_test_id);
    const history_id = useSelfTestStore((s) => s.history_id);
    const testId = stateTestId || storedTestId;

    const { ROUTERS_PATH } = RouterConstant;
    const selfTestService = new SelfTestService();

    const [testSummary, setTestSummary] = useState<ExerciseSummary[]>([]);
    const [correctCount, setCorrectCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!testId) return;

        const run = async () => {
            if (!history_id) {
                await submitSummary();
            }
            await fetchTestSummary();
        };
    
        run();
    }, [testId, location.key]);

    const submitSummary = async () => {
        try {
            setIsLoading(true);
            const stored = useSelfTestStore.getState().selected_answers;
            const answers = Object.values(stored).map((item) => ({
                user_id: 1,
                test_id: testId,
                exercise_id: item.exercise_id,
                selected_answer_id: item.selected_answer,
            }));
    
            const response = await selfTestService.upsert_user_self_test_answers(answers);
            if (response?.history_id) {
                useSelfTestStore.getState().setHistoryId(response.history_id);
            }

        } catch (err) {
            console.error("Error submitting answers or fetching summary", err);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTestSummary = async () => {
        try {
            setIsLoading(true);
            const response = await selfTestService.get_user_test_exercise_summary({
                user_id: 1,
                test_id: testId
            });
            const summary = response?.exercises || [];
            setTestSummary(summary);
            const correct = summary.filter((e: any) => e.answer_status === 1).length;
            setCorrectCount(correct);
    
        } catch (err) {
            console.error("Failed to load test summary", err);
        } finally {
            setIsLoading(false);
        }
    };
    

    const handleSelectExercise = (item: ExerciseSummary, idx: number) => {
        navigate(ROUTERS_PATH.SELFTESTANSWERPREVIEW, {
          state: { selectedExercise: item, testSummaryCount: testSummary.length, idx: idx},
        });
      };


    return (
        <div className="self-test-summary-page">
            <ChatHeader
                chatHeading={t("examSummary")}
                onBackClick={() => navigate(ROUTERS_PATH.CHOOSEEXERCISE)}
                currentIndex={testSummary.length}
                totalExercises={testSummary.length}
                isLoading={isLoading}
            />
            <div className="summary-header">
                <h3>{t("testOver")}!</h3>
                <h4>
                    {t("youAnswered")}
                    {
                        isLoading ? (
                            <div className="loaderBlock main-flex">
                                <img src={Loader} alt="Loading" />
                            </div>
                        ) : (
                            <span className='color correct-count-gap'>{correctCount}</span>
                        )
                    }
                    /
                    {
                        isLoading ? (
                            <div className="loaderBlock main-flex">
                                <img src={Loader} alt="Loading" />
                            </div>
                        ) : (
                            <span className='total-count-gap'>{testSummary.length}</span>
                        )
                    }
                    {t("questionsCorrectly")}
                </h4>
                <img src={examImg} alt="Exam" />
            </div>
            <div className="exercise-list">
                {isLoading ? (
                    <div className="loaderBlock">
                      <img src={Loader} alt="Loading" />
                    </div>
                ) : (
                    testSummary.map((item, idx) => (
                        <div
                            key={item.exercise_id}
                            className={`exercise-item ${item.answer_status === 1
                                    ? "correct"
                                    : item.answer_status === 2 || item.answer_status === 3
                                        ? "wrong"
                                        : ""
                                }`}
                            onClick={() => handleSelectExercise(item, idx)}
                        >
                            <div className="exercise-item-header">
                                <p>
                                    <span className="question-title">
                                        {t("question")} {idx + 1}
                                    </span>
                                    <span className="divider">•</span>
                                    <span className="answer-status">
                                        {item.answer_status === 1
                                            ? t("rightAnswer")
                                            : item.answer_status === 2 || item.answer_status === 3
                                                ? t("wrongAnswer")
                                                : ""}
                                    </span>
                                </p>

                                <button className="btn" onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectExercise(item, idx)
                                    }}
                                >
                                    <img src={arrowLeft} alt="Exam" />
                                </button>
                            </div>
                            <p>{item.exercise_display_title}</p>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}